<template>
  <div id="chart-image-container-wrapper" :class="{
    'left-aligned-chart': isChartLeftAligned,
    'auto-size-charts-enabled': isAutoSizeChartsCurrentlyActive
  }">
    <div
      id="freestar-horizontal-ad-container"
      v-if="areFreestarAdsDisplayed"
      v-show="shouldShowFreestarAdsAboveChart"
    >
      <FreestarAd ad-id-key="ATF" />
    </div>
    <div id="resize-dragging-preview" :style="resizeDraggingPreviewCSSStyles"></div>
    <div
      :id="CHART_IMAGE_CONTAINER_ID"
      :class="{
        'annotation-mode': isAnnotating,
        'annotation-mode-initialized': isChartNotesV2Initialized
      }"
    >
      <div id="chart-image-and-surrounding-ui-and-resize-dragging-handle-container">
        <div id="chart-image-and-surrounding-ui-container">
          <ChartStyleButtons />
          <div id="chart-image-and-inspector-container-outer">
            <div
              id="chart-image-and-inspector-container"
              :class="{
                'custom-right-click-dropdown-menu-disabled': isAnnotating || isActivatingAnnotationMode || wasErrorEncounteredWhileLoadingChartImage || layoutStore.isDragNDropping || isSymbolBeingViewedNotFound || isSymbolBeingViewedAnEODSymbolOnAnIntradayChart
              }"
              ref="chartImageAndInspectorContainer"
            >
              <div
                id="chart-image-container-inner"
                v-show="chartImageUrlString && !inspectorChartImageSource && !inspectorErrorTitle && !isSymbolBeingViewedNotFound && !isSymbolBeingViewedAnEODSymbolOnAnIntradayChart"
              >
                <div id="ChartNotesMenu"></div>
                <img
                  alt="SharpCharts Chart"
                  :src="chartImageUrlString"
                  v-show="!wasErrorEncounteredWhileLoadingChartImage"
                  :id="CHART_IMAGE_ID"
                  draggable="false"
                  ondragstart="return false;"
                  @load="handleChartImageLoad()"
                  @error="handleChartImageError($event, chartImageUrlString)"
                  @abort="handleChartImageAbort($event, chartImageUrlString)"
                  @cancel="handleChartImageCancel($event, chartImageUrlString)"
                  ref="chartImageElement"
                />
              </div>
              <div class="inspector-container" v-show="(inspectorChartImageSource || inspectorErrorTitle) && !isSymbolBeingViewedNotFound && !isSymbolBeingViewedAnEODSymbolOnAnIntradayChart">
                <img
                  alt="SharpCharts Chart"
                  :src="inspectorChartImageSource"
                  v-show="inspectorChartImageSource && !inspectorErrorTitle && !wasErrorEncounteredWhileLoadingChartImage"
                  draggable="false"
                  ondragstart="return false;"
                  @load="handleInspectorChartImageLoaded()"
                  @error="handleInspectorChartImageError($event, inspectorChartImageSource)"
                  @abort="handleInspectorChartImageAbort($event, inspectorChartImageSource)"
                  @cancel="handleInspectorChartImageCancel($event, inspectorChartImageSource)"
                />
                <canvas
                  v-show="inspectorChartImageSource && !inspectorErrorTitle && !wasErrorEncounteredWhileLoadingChartImage"
                  id="inspector-on-screen-canvas"
                  @mousemove="handleInspectorOnScreenCanvasMouseMove($event)"
                  @touchstart="handleInspectorOnScreenCanvasTouchStart($event)"
                  @touchmove="handleInspectorOnScreenCanvasTouchMove($event)"
                  @mousedown="handleInspectorOnScreenCanvasMouseDown($event)"
                  @mouseup="handleInspectorOnScreenCanvasMouseUp($event)"
                  ref="inspectorOnScreenCanvasElementRef"
                ></canvas>
                <p v-show="inspectorErrorTitle" class="inspector-error-message">
                  Hmm... An error occurred {{ inspectorErrorTitle }}.
                  Please reload this page.
                  If this error persists, please contact <a href="/support/techsupport.html">customer support</a> and let them know what happened.
                </p>
              </div>
              <div class="invalid-symbol-screen-container" v-show="isSymbolBeingViewedNotFound && !isSymbolBeingViewedAnEODSymbolOnAnIntradayChart">
                <div class="invalid-symbol-screen-header-icon"><i class="fas fa-fw fa-map-marker-question"></i></div>
                <h2 class="invalid-symbol-screen-header">
                  Hmm, we couldn't find data for
                  <span
                    class="invalid-symbol-screen-header-symbol"
                    :title="layoutStore.symbol"
                  >{{ layoutStore.symbol }}</span>
                </h2>
                <div>
                  <label class="sc3-form-label">TRY A NEW SYMBOL</label>
                  <input
                    type="text"
                    class="sc3-text-input"
                    placeholder="Enter a Symbol"
                    autocomplete="off"
                    v-model="symbol"
                    @keydown.enter="submitNewSymbol()"
                    ref="submitNewSymbolInputElement"
                  />
                </div>
                <p class="invalid-symbol-screen-lower-message">
                  If there's something you'd like us to add,
                  <a
                    class="sc3-button-link sc3-button-link-inline"
                    title="Symbol Request Form"
                    href="/support/symbolrequest.html"
                    target="_blank"
                  >Click&nbsp;Here</a>
                  to request a symbol
                </p>
              </div>
              <div class="invalid-symbol-screen-container" v-show="!isSymbolBeingViewedNotFound && isSymbolBeingViewedAnEODSymbolOnAnIntradayChart">
                <div class="invalid-symbol-screen-header-icon"><i class="fas fa-fw fa-exclamation-circle"></i></div>
                <h2 class="invalid-symbol-screen-header">
                  Unfortunately we don't have intraday data for
                  <span
                    class="invalid-symbol-screen-header-symbol"
                    :title="layoutStore.symbol"
                  >{{ layoutStore.symbol }}</span>
                </h2>
                <div>
                  <PeriodMenu
                    :is-inline="false"
                    label-text="TRY A DIFFERENT PERIOD"
                    html-id="invalid-symbol-screen-period-menu"
                    :should-automatically-disable-intraday-period-options="true"
                    :is-compact="false"
                    :is-white-and-grey="false"
                  />
                </div>
              </div>
            </div>
            <span
              v-show="!isAnnotating && !isActivatingAnnotationMode && !wasErrorEncounteredWhileLoadingChartImage && !layoutStore.isDragNDropping && chartImageUrlString && !isSymbolBeingViewedNotFound && !isSymbolBeingViewedAnEODSymbolOnAnIntradayChart"
              id="resize-dragging-handle"
              @mousedown="handleResizeDraggingHandleMouseDown($event)"
            ></span>
          </div>
        </div>
      </div>
      <template v-if="areFreestarAdsDisplayed">
        <div v-show="shouldShowFreestarAdsOnRightSideOfChart" id="freestar-side-ads-container">
          <div class="freestar-side-ads-column">
            <FreestarAd ad-id-key="RIGHT_1" />
            <FreestarAd ad-id-key="IN_CONTENT_MREC_PRIMIS_REPLACEMENT_CHART" />
            <FreestarAd ad-id-key="RIGHT_2" />
          </div>
          <div class="freestar-side-ads-column">
            <FreestarAd ad-id-key="RIGHT_SIDEWALL" />
          </div>
        </div>
      </template>
    </div>
  </div>
  <ul
    class="dropdown-menu custom-right-click-dropdown-menu"
    :class="{ 'dropdown-menu-open': isChartCustomRightClickDropdownMenuOpen }"
    ref="chartCustomRightClickDropdownMenuNode"
  >
    <li class="dropdown-menu-option">
      <button
        type="button"
        class="dropdown-menu-option-button"
        @click="launchSaveChartModal()"
        :disabled="isSymbolBeingViewedNotFound || isSymbolBeingViewedAnEODSymbolOnAnIntradayChart"
      >
        <i class="fas fa-fw fa-download"></i>
        &nbsp;Save Chart
      </button>
    </li>
    <li class="dropdown-menu-option">
      <button
        type="button"
        class="dropdown-menu-option-button"
        @click="launchSaveChartStyleModal()"
      >
        <i class="fas fa-fw fa-plus-circle"></i>
        &nbsp;Save ChartStyle
      </button>
    </li>
    <li class="dropdown-menu-option-divider"></li>
    <li class="dropdown-menu-option">
      <button
        type="button"
        class="dropdown-menu-option-button"
        @click="activateAnnotationMode()"
        :disabled="isSymbolBeingViewedNotFound || isSymbolBeingViewedAnEODSymbolOnAnIntradayChart || chartStore.chartType === CHART_TYPE_OPTION_PERF_CHART.value"
      >
        <i class="fas fa-fw fa-pencil-ruler" v-show="!isActivatingAnnotationMode"></i>
        <i class="fad fa-fw fa-spinner-third fa-spin" v-show="isActivatingAnnotationMode"></i>
        &nbsp;Annotate
      </button>
    </li>
    <li class="dropdown-menu-option-divider"></li>
    <li class="dropdown-menu-option">
      <button
        type="button"
        class="dropdown-menu-option-button"
        @click="launchCreatePriceAlertModal()"
        :disabled="isSymbolBeingViewedNotFound"
      >
        <i class="fas fa-fw fa-bell-plus"></i>
        &nbsp;Create Alert
      </button>
    </li>
    <li class="dropdown-menu-option-divider"></li>
    <li class="dropdown-menu-option">
      <a
        class="dropdown-menu-option-button"
        :href="chartImageUrlString"
        :download="downloadFileName"
        target="_blank"
        rel="noopener noreferrer"
      >
        <i class="fas fa-fw fa-file-download"></i>
        &nbsp;Download Chart Image
      </a>
    </li>
    <li class="dropdown-menu-option">
      <a
        :href="chartImageUrlString"
        target="_blank"
        class="dropdown-menu-option-button"
      >
        <i class="far fa-fw fa-external-link"></i>
        &nbsp;Open Chart Image In New Tab
      </a>
    </li>
    <li class="dropdown-menu-option" v-if="doesBrowserSupportCopyingImagesToClipboard && chartImageElement">
      <button
        type="button"
        class="dropdown-menu-option-button"
        @click="copyImageToClipboard(chartImageElement)"
      >
        <i class="far fa-fw fa-copy"></i>
        &nbsp;Copy Chart Image
      </button>
    </li>
    <li v-show="chartBeingViewed" class="dropdown-menu-option">
      <button
        type="button"
        class="dropdown-menu-option-button"
        @click="deleteChart()"
        :disabled="isChartBeingViewedReadonly"
      >
        <i class="fas fa-fw fa-trash-alt"></i>
        &nbsp;Delete Chart
      </button>
    </li>
    <li class="dropdown-menu-option-divider"></li>
    <li class="dropdown-menu-option">
      <button
        type="button"
        class="dropdown-menu-option-button"
        @click="launchLinkToChartModal()"
      >
        <i class="far fa-fw fa-link"></i>
        &nbsp;Link To Chart
      </button>
    </li>
  </ul>
</template>

<script lang="ts" setup>
  import { ref, watch, onMounted, onUnmounted } from 'vue';
  import { storeToRefs } from 'pinia';
  import { useChartStore } from '@/stores/chartStore';
  import { useLayoutStore } from '@/stores/layoutStore';
  import { useAuthentication } from '@/composables/useAuthentication';
  import { useChartNotesV2 } from '@/composables/useChartNotesV2';
  import { useDownloadFileName } from '@/composables/useDownloadChart';
  import { useChartDimensions } from '@/composables/useChartDimensions';
  import { useFreestarAds } from '@/composables/useFreestarAds';
  import { useCopyImageToClipboard } from '@/composables/useCopyImageToClipboard';
  import { useWorkbenchSelections } from '@/composables/useWorkbenchSelections';
  import { useActionResultMessages } from '@/composables/useActionResultMessages';
  import { useLoadChartsAndChartStyles } from '@/composables/useLoadChartsAndChartStyles';
  import { useSymbolInformation } from '@/composables/useSymbolInformation';
  import { useScreenSizing } from '@/composables/useScreenSizing';
  import { useDropdownMenu } from '@/composables/useDropdownMenu';
  import { usePerfChartSymbols } from '@/composables/usePerfChartSymbols';
  import { useModals } from '@/composables/useModals';
  import { getSharpCharts2ChartImageUrlData } from '@/composables/getSharpCharts2ChartImageUrlData';
  import { updateChart } from '@/composables/updateChart';
  import { refreshChartLists } from '@/composables/refreshChartLists';
  import { loadChartsForChartList } from '@/composables/loadChartsForChartList';
  import { isAnHtmlUIElementFocused } from '@/utils/isAnHtmlUIElementFocused';
  import { waitForDomToRepaint } from '@/utils/waitForDomToRepaint';
  import { getStyleUrlSearchParams } from '@/utils/getStyleUrlSearchParams';
  import Inspector from '@/helper-classes/inspector/Inspector';
  import ChartStyleButtons from '@/components/page-skeleton/ChartStyleButtons.vue';
  import FreestarAd from '@/components/page-skeleton/FreestarAd.vue';
  import PeriodMenu from '@/components/select-menus/PeriodMenu.vue';
  import { CHART_IMAGE_ID, CHART_IMAGE_CONTAINER_ID } from '@/enums/html-ids';
  import { CHART_TYPE_OPTION_PERF_CHART } from '@/enums/chart-type-options';

  const chartStore = useChartStore();
  const layoutStore = useLayoutStore();
  const { isLoggedIn } = useAuthentication();
  const {
    chartImageUrlString,
    inspectorDataString
  } = storeToRefs(layoutStore);
  const {
    isActivatingAnnotationMode,
    isAnnotating,
    isChartNotesV2Initialized,
    activateAnnotationMode
  } = useChartNotesV2();
  const { downloadFileName } = useDownloadFileName();
  const {
    CHART_DIMENSION_CONSTANTS,
    isChartLeftAligned,
    isAutoSizeChartsCurrentlyActive,
    resizeDraggingPreviewCSSStyles,
    handleResizeDraggingHandleMouseDown
  } = useChartDimensions();
  const { areFreestarAdsDisplayed } = useFreestarAds();
  const { doesBrowserSupportCopyingImagesToClipboard, copyImageToClipboard } = useCopyImageToClipboard();
  const { chartBeingViewed, isChartBeingViewedReadonly } = useWorkbenchSelections();
  const { addGreenActionResultMessage, addRedActionResultMessage } = useActionResultMessages();
  const { loadChart } = useLoadChartsAndChartStyles();
  const { isSymbolBeingViewedNotFound, isSymbolBeingViewedAnEODSymbolOnAnIntradayChart } = useSymbolInformation();
  const { shouldShowFreestarAdsOnRightSideOfChart, shouldShowFreestarAdsAboveChart } = useScreenSizing();
  const { MAXIMUM_NUMBER_OF_PERF_CHART_SYMBOLS, getPerfChartSymbols } = usePerfChartSymbols();
  const {
    launchCreateAccountModal,
    launchCreatePriceAlertModal,
    launchSaveChartModal,
    launchSaveChartAsModal,
    launchSaveChartStyleModal,
    launchDeleteChartModal,
    launchLinkToChartModal
  } = useModals();
  const {
    CHART_IMAGE_CONTAINER_PADDING_TOP,
    CHART_IMAGE_CONTAINER_PADDING_RIGHT,
    CHART_IMAGE_CONTAINER_PADDING_BOTTOM,
    CHART_IMAGE_CONTAINER_PADDING_LEFT,
    CHART_STYLE_BUTTON_CONTAINER_WIDTH,
    RESIZE_DRAGGING_HANDLE_DIAMETER,
    WHITE_SPACE_BETWEEN_CHART_STYLE_BUTTONS_AND_CHART,
    RESIZE_DRAGGING_PREVIEW_CSS_LEFT_ALIGNED_LEFT_VALUE,
    RESIZE_DRAGGING_PREVIEW_CSS_CENTER_ALIGNED_LEFT_VALUE,
    WIDTH_OF_CHART_NOTES_UI,
    INVALID_SYMBOL_ERROR_SCREEN_WIDTH,
    INVALID_SYMBOL_ERROR_SCREEN_HEIGHT
  } = CHART_DIMENSION_CONSTANTS;
  const chartImageElement = ref<HTMLImageElement>();
  const chartImageAndInspectorContainer = ref<HTMLDivElement>();
  const chartCustomRightClickDropdownMenuNode = ref<HTMLUListElement>();
  const inspectorOnScreenCanvasElementRef = ref<HTMLCanvasElement>();
  const inspectorOffScreenCanvasElement = document.createElement('canvas');
  const inspectorChartImageSource = ref('');
  const inspectorErrorTitle = ref('');
  let inspector: Inspector | undefined;
  let isChartCurrentlyBeingSaved = false;
  const wasErrorEncounteredWhileLoadingChartImage = ref(false);
  const symbol = ref('');
  const submitNewSymbolInputElement = ref<HTMLInputElement>();

  function submitNewSymbol() {
    submitNewSymbolInputElement.value?.blur();

    if (
      isActivatingAnnotationMode.value ||
      isAnnotating.value
    ) {
      return;
    }

    const formattedSymbol = symbol.value.replace(/\s/g, '').toUpperCase();
    let perfChartSymbols = getPerfChartSymbols(formattedSymbol).slice(0, MAXIMUM_NUMBER_OF_PERF_CHART_SYMBOLS);
    const mainChartSymbol = perfChartSymbols.at(0)?.symbol ?? '';
    perfChartSymbols = perfChartSymbols.slice(1);

    if (mainChartSymbol) {
      if (perfChartSymbols.length) {
        chartStore.$patch({
          symbol: mainChartSymbol,
          chartType: CHART_TYPE_OPTION_PERF_CHART.value,
          perfChartSymbols,
          latestLoadPerfChartTimeStamp: Date.now()
        });
      } else {
        chartStore.$patch({ symbol: mainChartSymbol });
      }
    }

    resetSymbolInput();
  }

  function resetSymbolInput() {
    symbol.value = '';
  }

  async function handleIsSymbolBeingViewedNotFoundChanged() {
    resetSymbolInput();

    await waitForDomToRepaint();

    submitNewSymbolInputElement.value?.focus({ preventScroll: true });
  }

  function handleInspectorOnScreenCanvasMouseMove(e: MouseEvent) {
    inspector?.setMouseXAndMouseY(e, false);
    inspector?.setDirection();
    inspector?.paint();
  }

  function handleInspectorOnScreenCanvasTouchStart(e: TouchEvent) {
    if (!inspector) {
      return;
    }

    if (e.targetTouches.length !== 1) {
      return;
    }

    inspector.setMouseXAndMouseY(e.targetTouches[0], false);

    if (inspector.isMouseOverInspectorBounds()) {
      e.preventDefault();
    }

    inspector.paint();
  }

  function handleInspectorOnScreenCanvasTouchMove(e: TouchEvent) {
    if (!inspector) {
      return;
    }

    if (e.changedTouches.length !== 1) {
      return;
    }

    inspector.setMouseXAndMouseY(e.changedTouches[0], false);

    if (inspector.isMouseOverInspectorBounds()) {
      e.preventDefault();
    }

    inspector.paint();
  }

  function handleInspectorOnScreenCanvasMouseDown(e: MouseEvent) {
    inspector?.setMouseXAndMouseY(e, true);
    inspector?.paint();
  }

  function handleInspectorOnScreenCanvasMouseUp(e: MouseEvent) {
    if (!inspector) {
      return;
    }

    inspector.mouseDownX = null;
    inspector.mouseDownY = null;
    inspector.direction = null;

    inspector.paint();
  }

  function handleBodyKeyDown(e: KeyboardEvent) {
    if (
      layoutStore.modalType ||
      layoutStore.isDragNDropping ||
      isActivatingAnnotationMode.value ||
      isAnnotating.value ||
      isSymbolBeingViewedNotFound.value ||
      isSymbolBeingViewedAnEODSymbolOnAnIntradayChart.value
    ) {
      return;
    }

    if (
      e.ctrlKey &&
      e.key.toLowerCase() === 's' &&
      e.shiftKey &&
      !e.metaKey
    ) {
      e.preventDefault();
      launchSaveChartAsModal();
    }

    if (
      e.ctrlKey &&
      e.key.toLowerCase() === 's' &&
      !e.shiftKey &&
      !e.metaKey
    ) {
      e.preventDefault();
      handleSave();
    }

    if (!inspector || isAnHtmlUIElementFocused()) {
      return;
    }

    if (e.shiftKey) {
      inspector.shiftDown = true;
    }

    if (e.ctrlKey || e.metaKey) {
      inspector.ctrlDown = true;
    }

    inspector.paint();
  }

  async function handleSave() {
    if (isChartCurrentlyBeingSaved) {
      return;
    }

    if (
      !chartBeingViewed.value ||
      isChartBeingViewedReadonly.value
    ) {
      launchSaveChartAsModal();
      return;
    }

    isChartCurrentlyBeingSaved = true;
    const { chartListListNum, recid } = chartBeingViewed.value;

    try {
      const sharpCharts2ChartImageUrlData = await getSharpCharts2ChartImageUrlData(true);
      const styleUrlSearchParams = getStyleUrlSearchParams(sharpCharts2ChartImageUrlData);

      await updateChart(recid, { styleUrlSearchParams });
      await refreshChartLists();
      await loadChartsForChartList(undefined, chartListListNum, true);
      addGreenActionResultMessage('Success! Your chart has been saved!');
      await loadChart(recid);
    } catch (e) {
      window.sc3WorkbenchLogger.error(`handleSave failed from keyboard shortcut - recid: ${ recid }, chartListListNum: ${ chartListListNum }`, e);

      addRedActionResultMessage('Failed to save chart. Please try again.');
    }

    isChartCurrentlyBeingSaved = false;
  }

  function handleBodyKeyUp(e: KeyboardEvent) {
    if (!inspector) {
      return;
    }

    if (!e.shiftKey) {
      inspector.shiftDown = false;
    }

    if (!e.ctrlKey && !e.metaKey) {
      inspector.ctrlDown = false;
    }

    inspector.paint();
  }

  function destroyInspector() {
    if (inspector) {
      inspector.isDestroyed = true;
    }

    inspector = undefined;
    inspectorChartImageSource.value = '';
    inspectorErrorTitle.value = '';

    layoutStore.$patch({ isInspectorInUse: false });
  }

  function handleInspectorDataStringChanged() {
    if (!inspectorOnScreenCanvasElementRef.value) {
      return;
    }

    destroyInspector();

    if (!inspectorDataString.value) {
      return;
    }

    try {
      const chartDataIndex = inspectorDataString.value.indexOf('<chartdata>');

      if (inspectorDataString.value.indexOf('img') !== 0) {
        throw new Error('Failed to load image data.');
      }

      if (chartDataIndex === -1) {
        // Error image
        inspectorChartImageSource.value = `data:${inspectorDataString.value}`;
        return;
      }

      const chartDataSource = inspectorDataString.value.substring(0, chartDataIndex);
      inspectorChartImageSource.value = `data:${chartDataSource}`;
      const chartDataString = inspectorDataString.value.substring(chartDataIndex);
      inspector = new Inspector({
        chartDataString,
        onScreenCanvasElement: inspectorOnScreenCanvasElementRef.value,
        offScreenCanvasElement: inspectorOffScreenCanvasElement
      });
    } catch (e: unknown) {
      destroyInspector();

      if (e && typeof e === 'object' && 'message' in e && typeof e.message === 'string') {
        if (e.message.toLowerCase().indexOf('failed to load image data') >= 0) {
          inspectorErrorTitle.value = 'loading the image data.';
          return;
        }

        if (e.message.toLowerCase().indexOf('no chart data') >= 0) {
          inspectorErrorTitle.value = 'loading the chart data.';
          return;
        }

        if (e.message.toLowerCase().indexOf('no price data') >= 0) {
          inspectorErrorTitle.value = 'loading the price data.';
          return;
        }
      }

      inspectorErrorTitle.value = 'processing the server\'s response';
    }
  }

  function handleChartImageLoad() {
    if (layoutStore.isInspecting) {
      return;
    }

    if (!layoutStore.hasChartImageLoadAttemptCompleted) {
      layoutStore.$patch({ hasChartImageLoadAttemptCompleted: true });
    }

    wasErrorEncounteredWhileLoadingChartImage.value = false;
  }

  function handleChartImageError(e: Event, imgSrc: string) {
    if (layoutStore.isInspecting) {
      return;
    }

    if (!layoutStore.hasChartImageLoadAttemptCompleted) {
      layoutStore.$patch({ hasChartImageLoadAttemptCompleted: true });
    }

    if (
      !imgSrc ||
      !chartImageUrlString.value
    ) {
      return;
    }

    wasErrorEncounteredWhileLoadingChartImage.value = true;

    window.sc3WorkbenchLogger.error(`Error event occurred while loading chart image: ${typeof imgSrc} ${imgSrc}.`, e);
  }

  function handleChartImageAbort(e: Event, imgSrc: string) {
    if (layoutStore.isInspecting) {
      return;
    }

    if (!layoutStore.hasChartImageLoadAttemptCompleted) {
      layoutStore.$patch({ hasChartImageLoadAttemptCompleted: true });
    }

    if (
      !imgSrc ||
      !chartImageUrlString.value
    ) {
      return;
    }

    wasErrorEncounteredWhileLoadingChartImage.value = true;

    window.sc3WorkbenchLogger.error(`Abort event occurred while loading chart image: ${typeof imgSrc} ${imgSrc}.`, e);
  }

  function handleChartImageCancel(e: Event, imgSrc: string) {
    if (layoutStore.isInspecting) {
      return;
    }

    if (!layoutStore.hasChartImageLoadAttemptCompleted) {
      layoutStore.$patch({ hasChartImageLoadAttemptCompleted: true });
    }

    if (
      !imgSrc ||
      !chartImageUrlString.value
    ) {
      return;
    }

    wasErrorEncounteredWhileLoadingChartImage.value = true;

    window.sc3WorkbenchLogger.error(`Cancel event occurred while loading chart image: ${typeof imgSrc} ${imgSrc}.`, e);
  }

  function handleInspectorChartImageLoaded() {
    if (!layoutStore.isInspecting) {
      return;
    }

    if (!layoutStore.hasChartImageLoadAttemptCompleted) {
      layoutStore.$patch({ hasChartImageLoadAttemptCompleted: true });
    }

    wasErrorEncounteredWhileLoadingChartImage.value = false;
  }

  function handleInspectorChartImageError(e: Event, imgSrc: string) {
    if (!layoutStore.isInspecting) {
      return;
    }

    if (!layoutStore.hasChartImageLoadAttemptCompleted) {
      layoutStore.$patch({ hasChartImageLoadAttemptCompleted: true });
    }

    if (
      !imgSrc ||
      !inspectorChartImageSource.value
    ) {
      return;
    }

    wasErrorEncounteredWhileLoadingChartImage.value = true;

    window.sc3WorkbenchLogger.error(`Error event occurred while loading inspector chart image: ${typeof imgSrc} ${imgSrc}.`, e);
  }

  function handleInspectorChartImageAbort(e: Event, imgSrc: string) {
    if (!layoutStore.isInspecting) {
      return;
    }

    if (!layoutStore.hasChartImageLoadAttemptCompleted) {
      layoutStore.$patch({ hasChartImageLoadAttemptCompleted: true });
    }

    if (
      !imgSrc ||
      !inspectorChartImageSource.value
    ) {
      return;
    }

    wasErrorEncounteredWhileLoadingChartImage.value = true;

    window.sc3WorkbenchLogger.error(`Abort event occurred while loading inspector chart image: ${typeof imgSrc} ${imgSrc}.`, e);
  }

  function deleteChart() {
    if (!isLoggedIn.value) {
      launchCreateAccountModal();
      return;
    }

    if (
      !chartBeingViewed.value ||
      isChartBeingViewedReadonly.value
    ) {
      return;
    }

    launchDeleteChartModal(chartBeingViewed.value.recid);
  }

  function handleInspectorChartImageCancel(e: Event, imgSrc: string) {
    if (!layoutStore.isInspecting) {
      return;
    }

    if (!layoutStore.hasChartImageLoadAttemptCompleted) {
      layoutStore.$patch({ hasChartImageLoadAttemptCompleted: true });
    }

    if (
      !imgSrc ||
      !inspectorChartImageSource.value
    ) {
      return;
    }

    wasErrorEncounteredWhileLoadingChartImage.value = true;

    window.sc3WorkbenchLogger.error(`Cancel event occurred while loading inspector chart image: ${typeof imgSrc} ${imgSrc}.`, e);
  }

  function handleCompleteChartImageUrlStringChanged() {
    wasErrorEncounteredWhileLoadingChartImage.value = false;
  }

  function handleInspectorChartImageSourceChanged() {
    wasErrorEncounteredWhileLoadingChartImage.value = false;
  }

  watch(chartImageUrlString, handleCompleteChartImageUrlStringChanged);
  watch(inspectorChartImageSource, handleInspectorChartImageSourceChanged);
  watch(isSymbolBeingViewedNotFound, handleIsSymbolBeingViewedNotFoundChanged);

  const { isDropdownMenuOpen: isChartCustomRightClickDropdownMenuOpen } = useDropdownMenu({
    isCustomRightClickMenu: true,
    customRightClickMenuContainerNode: chartImageAndInspectorContainer,
    dropdownMenuToggleButtonNode: null,
    dropdownMenuNode: chartCustomRightClickDropdownMenuNode,
    onClose: null,
    shouldBlurDropdownMenuToggleButtonNodeOnClose: false
  });

  onMounted(() => {
    handleInspectorDataStringChanged();
    watch(inspectorDataString, handleInspectorDataStringChanged);
    document.body.addEventListener('keydown', handleBodyKeyDown);
    document.body.addEventListener('keyup', handleBodyKeyUp);
    layoutStore.$patch({ isChartComponentMounted: true });

    onUnmounted(() => {
      destroyInspector();
      document.body.removeEventListener('keydown', handleBodyKeyDown);
      document.body.removeEventListener('keyup', handleBodyKeyUp);
    });
  });
</script>

<style scoped>
  #chart-image-container-wrapper {
    position: relative;
    text-align: center;
    padding-top: v-bind(CHART_IMAGE_CONTAINER_PADDING_TOP + 'px');
    padding-right: v-bind(CHART_IMAGE_CONTAINER_PADDING_RIGHT + 'px');
    padding-left: v-bind(CHART_IMAGE_CONTAINER_PADDING_LEFT + 'px');
    padding-bottom: v-bind(CHART_IMAGE_CONTAINER_PADDING_BOTTOM + 'px');
  }

  #chart-image-container {
    display: flex;
    justify-content: center;
  }

  #chart-image-container-wrapper.left-aligned-chart #chart-image-container {
    justify-content: start;
  }

  #chart-image-and-surrounding-ui-and-resize-dragging-handle-container {
    max-width: 100%;
  }

  #chart-image-and-surrounding-ui-container {
    display: flex;
    gap: v-bind(WHITE_SPACE_BETWEEN_CHART_STYLE_BUTTONS_AND_CHART + 'px');
  }

  #chart-image-and-inspector-container-outer {
    position: relative;
    height: fit-content;
    max-width: calc(100% - v-bind((CHART_STYLE_BUTTON_CONTAINER_WIDTH + WHITE_SPACE_BETWEEN_CHART_STYLE_BUTTONS_AND_CHART) + 'px'));
  }

  #chart-image-container.annotation-mode #chart-image-and-inspector-container-outer {
    z-index: 1001;
    left: v-bind((WIDTH_OF_CHART_NOTES_UI - CHART_IMAGE_CONTAINER_PADDING_LEFT - CHART_STYLE_BUTTON_CONTAINER_WIDTH - WHITE_SPACE_BETWEEN_CHART_STYLE_BUTTONS_AND_CHART) + 'px');
  }

  #chart-image-and-inspector-container {
    overflow: auto;
  }

  #chart-image-and-inspector-container > div {
    box-shadow: rgba(0, 0, 0, 0.1) -0.8px 0px 4px 0px;
  }

  .annotation-mode.annotation-mode-initialized #chart-image-and-inspector-container {
    overflow: visible;
  }

  #ChartNotesMenu {
    position: absolute;
    top: 1px;
    bottom: 0px;
    left: calc(-1 * v-bind(WIDTH_OF_CHART_NOTES_UI + 'px'));
    width: 35px;
    background: transparent;
    display: none;
  }

  .auto-size-charts-enabled #chart-image-container-inner,
  .auto-size-charts-enabled #chart-image {
    max-width: 100%;
  }

  img {
    box-shadow: -2px 0px 8px 4px rgb(0 0 0 / 15%);
    user-drag: none;
    -webkit-user-drag: none;
    user-select: none;
    -moz-user-select: none;
    -webkit-user-select: none;
    -ms-user-select: none;
  }

  .chart-image-container-loading-spinner {
    display: inline-block;
  }

  #resize-dragging-handle {
    position: absolute;
    display: block;
    bottom: calc(-1 * v-bind(RESIZE_DRAGGING_HANDLE_DIAMETER + 'px') / 2);
    right: calc(-1 * v-bind(RESIZE_DRAGGING_HANDLE_DIAMETER + 'px') / 2);
    width: v-bind(RESIZE_DRAGGING_HANDLE_DIAMETER + 'px');
    height: v-bind(RESIZE_DRAGGING_HANDLE_DIAMETER + 'px');
    background-color: black;
    border: none;
    border-radius: 50%;
  }

  #resize-dragging-preview {
    display: none;
    position: absolute;
    left: 0px;
    right: 0px;
    border: 4px solid black;
    margin: 0px auto;
  }

  #chart-image-container-wrapper.left-aligned-chart #resize-dragging-preview {
    left: v-bind(RESIZE_DRAGGING_PREVIEW_CSS_LEFT_ALIGNED_LEFT_VALUE + 'px');
    right: unset;
    margin: 0px;
  }

  #chart-image-container-wrapper:not(.left-aligned-chart) #resize-dragging-preview {
    left: v-bind(RESIZE_DRAGGING_PREVIEW_CSS_CENTER_ALIGNED_LEFT_VALUE + 'px');
  }

  #resize-dragging-handle,
  #resize-dragging-preview {
    z-index: 996;
    user-select: contain;
    cursor: move;
  }

  .inspector-container {
    display: inline-block;
    position: relative;
    text-align: left;
    padding: 0px;
    margin: 0px;
  }

  #inspector-on-screen-canvas {
    position: absolute;
    top: 0px;
    left: 0px;
  }

  #freestar-side-ads-container {
    display: flex;
  }

  .freestar-side-ads-column {
    text-align: left;
    padding: 0px 12px;
  }

  .freestar-side-ads-column:first-child {
    padding-right: 0px;
  }

  #freestar-horizontal-ad-container {
    display: flex;
    overflow: auto;
    width: 100%;
    justify-content: center;
  }

  #freestar-horizontal-ad-container .freestar-ad {
    margin-bottom: 5px !important;
  }

  .invalid-symbol-screen-container {
    width: v-bind(INVALID_SYMBOL_ERROR_SCREEN_WIDTH + 'px');
    max-width: 100%;
    height: v-bind(INVALID_SYMBOL_ERROR_SCREEN_HEIGHT + 'px');
    background-color: #f2f5f7;
    text-align: center;
    padding: 45px 60px;
    display: flex;
    flex-direction: column;
    justify-content: space-evenly;
  }

  .invalid-symbol-screen-header-icon {
    font-size: 45px;
    color: #f9690e;
  }

  .invalid-symbol-screen-header {
    font-size: 30px;
    font-weight: 700;
    font-family: 'Roboto Condensed', 'Lato', 'Helvetica Neue', Helvetica, Arial, sans-serif;
    color: #333333;
  }

  .invalid-symbol-screen-header-symbol {
    color: #f9690e;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: inline-block;
    max-width: 100%;
    overflow: hidden;
    vertical-align: top;
  }

  .invalid-symbol-screen-lower-message {
    font-size: 16px;
    color: #5a6672;
  }

  .invalid-symbol-screen-lower-message a {
    font-size: 16px;
  }
</style>
